import { Button, FilterIcon, Modal } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigation } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { FilterActions } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector/filterActions';
import {
  useBloomFilters,
  useProductSearchFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import type { SortOptions } from '@jane/bloom/types/sort';
import { getFiltersCount } from '@jane/bloom/utils/filters/getFiltersCount';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { getFacetRange } from '@jane/bloom/viewModels/facetRange';
import type { ProductSearchFiltersViewModel } from '@jane/bloom/viewModels/productSearchFilters';

import { ModalButtonFilterSection } from './allFiltersModal/buttonFilterSection';
import { DealsAndRewardsSection } from './allFiltersModal/dealsAndRewardsSection';
import {
  ModalFilterEntrypoint,
  ModalFilterSection,
} from './allFiltersModal/modalFiltersSection';
import { PotencySection } from './allFiltersModal/potencySection';
import { RatingSection } from './allFiltersModal/ratingSection';
import { SortSection } from './allFiltersModal/sortSection';

import styles from './mobileFiltersModal.module.css';

export interface MobileFiltersModalProps {
  availableProductsCount?: number;
  currentSort: SortOptions;
  sortOptions: LabeledOption<SortOptions>[];
}

export const MobileFiltersModal = ({
  availableProductsCount,
  currentSort,
  sortOptions,
}: MobileFiltersModalProps) => {
  const {
    getPotencyFilterKeysNonEmpty,
    getProductSearchFilterOptions,
    janeGoldFilterOption,
    productSearchFilters,
  } = useProductSearchFilters();
  const { shouldShowGold, sortedAllDisplayFilterKey } = useBloomFilters();
  const { clearAllFilters, searchParams, setSearchParams } =
    useSearchParamFilters();
  const [resetting, setResetting] = useState(false);
  const { state } = useNavigation();

  const [originalFilters, setOriginalFilters] = useState({});

  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('default');

  const validPotencySliderKeys = getPotencyFilterKeysNonEmpty().filter(
    (key) => {
      const filterList = getProductSearchFilterOptions(key);
      const { rangeMax, rangeMin } = getFacetRange(filterList);
      return rangeMax > rangeMin;
    }
  );

  const activeFiltersCount = useMemo(
    () => getFiltersCount({ searchParams, sortOptions }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  );

  const [saleItem] = getProductSearchFilterOptions('sale');
  const goldItem = shouldShowGold ? janeGoldFilterOption : undefined;

  const sections = sortedAllDisplayFilterKey
    .filter(
      (filterKey) =>
        ['ratings', 'potencies', 'prices', 'weights', 'specials'].includes(
          filterKey
        ) ||
        productSearchFilters?.[filterKey as keyof ProductSearchFiltersViewModel]
          ?.length ||
        -1 > 0
    )
    .map((filterKey) => {
      switch (filterKey) {
        case 'ratings': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <RatingSection key={filterKey} />
            )
          );
        }
        case 'potencies': {
          return (
            validPotencySliderKeys.length > 0 && (
              <PotencySection key={filterKey} resetting={resetting} />
            )
          );
        }
        case 'prices': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection key={filterKey} filterKey={filterKey} />
            )
          );
        }
        case 'brands': {
          return (
            !isEmpty(productSearchFilters?.['brands']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('brands')}
              />
            )
          );
        }
        case 'terpenes': {
          return (
            !isEmpty(productSearchFilters?.['terpenes']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('terpenes')}
              />
            )
          );
        }
        case 'jane-gold': {
          return false;
        }
        default: {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection
                key={filterKey}
                filterKey={filterKey}
                icon
                shouldShowDividerBelow={
                  sortedAllDisplayFilterKey[
                    sortedAllDisplayFilterKey.length - 1
                  ] !== filterKey
                }
              />
            )
          );
        }
      }
    });

  useEffect(() => {
    if (resetting && state === 'idle') {
      setResetting(false);
    }
  }, [resetting, state]);

  const renderView = (isMobile: boolean) => {
    switch (selectedTab) {
      case 'brands':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      case 'terpenes':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      default:
        return (
          <>
            <Modal.Header
              title="Filters and sort"
              textAlign="center"
              branded={false}
            />
            <Modal.Content>
              <SortSection
                isMobile={isMobile}
                sortOptions={sortOptions}
                currentSort={currentSort}
              />
              {(goldItem || saleItem) && (
                <DealsAndRewardsSection
                  saleItem={saleItem}
                  goldItem={goldItem}
                />
              )}
              {sections}
            </Modal.Content>
          </>
        );
    }
  };

  return (
    <div className={styles['mobileFiltersButton']}>
      <Button.Icon
        variant="tertiary"
        icon={<FilterIcon />}
        ariaLabel="Filter and Sort"
        onClick={() => {
          setOriginalFilters(searchParams);
          setOpen(true);
        }}
      />
      {activeFiltersCount > 0 && (
        <span
          role="status"
          aria-label={`${activeFiltersCount} active filters`}
          className={styles['filterCount']}
        >
          {activeFiltersCount}
        </span>
      )}
      <ClientOnly>
        {(isMobile) => (
          <Modal
            background="grays-white"
            open={open}
            onRequestClose={
              selectedTab === 'default'
                ? () => {
                    setOpen(false);
                    setSearchParams(originalFilters);
                  }
                : () => setSelectedTab('default')
            }
            className={styles['mobileFiltersModal']}
            variant="full-screen"
            overlayClose
          >
            {renderView(isMobile)}
            <Modal.Footer>
              <FilterActions
                availableProductsCount={availableProductsCount}
                fullButtons={{ show: true }}
                handleClearAll={clearAllFilters}
                handleClose={() => setOpen(false)}
              />
            </Modal.Footer>
          </Modal>
        )}
      </ClientOnly>
    </div>
  );
};
